import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

const TRUE_STRINGS = new Set(['true', '1', 'yes', 'y', 'on', 't', 'active']);
const FALSE_STRINGS = new Set(['false', '0', 'no', 'n', 'off', 'f', 'inactive']);

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function coerceBoolean(
  value: unknown,
  defaultValue: boolean | null = null,
  options: { allowEmpty?: boolean } = {},
): boolean | null {
  const { allowEmpty = true } = options;

  if (value === null || value === undefined) {
    if (!allowEmpty) throw new Error('empty');
    return defaultValue;
  }

  if (typeof value === 'boolean') return value;

  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value !== 0;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      if (!allowEmpty) throw new Error('empty');
      return defaultValue;
    }
    const lowered = trimmed.toLowerCase();
    if (TRUE_STRINGS.has(lowered)) return true;
    if (FALSE_STRINGS.has(lowered)) return false;
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
      return numeric !== 0;
    }
    return defaultValue;
  }

  return defaultValue;
}

/**
 * Clamps a number between min and max values.
 * Returns min if value is NaN or not finite.
 */
export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
}

export function coerceNumber(
  value: unknown,
  defaultValue: number | null = null,
  options: { allowEmpty?: boolean; allowNone?: boolean } = {},
): number | null {
  const { allowEmpty = true, allowNone = true } = options;

  if (value === null || value === undefined) {
    if (!allowNone) throw new Error('empty');
    return defaultValue;
  }

  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      if (!allowEmpty) throw new Error('empty');
      return defaultValue;
    }
    const normalized = trimmed.replace(/,/g, '');
    const parsed = Number(normalized);
    if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
      return parsed;
    }
    return defaultValue;
  }

  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }

  return defaultValue;
}
